import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../../App.css';
import {
  adminRequest,
  currentAdminSession,
  signInAdmin,
  signOutAdmin,
} from '../../utils/adminApi';

const formatMoney = (cents) => new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  maximumFractionDigits: 0,
}).format((cents || 0) / 100);

const fulfilmentOptions = ['processing', 'packing', 'shipped', 'delivered'];

function AdminDashboard() {
  const [session, setSession] = useState(currentAdminSession);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(Boolean(session));
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyId, setBusyId] = useState('');

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await adminRequest('orders');
      setOrders(result.orders);
    } catch (requestError) {
      setError(requestError.message);
      if (requestError.message.includes('sign in')) {
        signOutAdmin();
        setSession(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (session) loadOrders();
  }, [loadOrders, session]);

  const summary = useMemo(() => {
    const paid = orders.filter((order) => order.salesCategory === 'paid');
    const abandoned = orders.filter((order) => order.salesCategory === 'abandoned');
    return {
      revenue: paid.reduce((total, order) => total + order.amount, 0),
      paid: paid.length,
      abandoned: abandoned.length,
      bottles: paid.reduce((total, order) => total + order.quantity, 0),
    };
  }, [orders]);

  const visibleOrders = orders.filter((order) => filter === 'all' || order.salesCategory === filter);
  const conversion = summary.paid + summary.abandoned
    ? Math.round((summary.paid / (summary.paid + summary.abandoned)) * 100)
    : 0;
  const preparing = orders.filter(
    (order) => order.salesCategory === 'paid' && ['processing', 'packing'].includes(order.fulfillmentStatus)
  ).length;
  const dispatched = orders.filter(
    (order) => order.salesCategory === 'paid' && ['shipped', 'delivered'].includes(order.fulfillmentStatus)
  ).length;
  const feedbackOptions = [
    ['price', 'Price'],
    ['technical', 'Payment / technical'],
    ['delivery', 'Delivery'],
    ['later', 'Not ready yet'],
    ['other', 'Other'],
  ];
  const feedbackTotal = orders.filter((order) => order.feedbackReason).length;
  const feedbackStats = feedbackOptions.map(([key, label]) => {
    const count = orders.filter((order) => order.feedbackReason === key).length;
    return { key, label, count, percent: feedbackTotal ? Math.round((count / feedbackTotal) * 100) : 0 };
  });

  const perform = async (id, action, successMessage) => {
    setBusyId(id);
    setError('');
    setNotice('');
    try {
      await action();
      setNotice(successMessage);
      await loadOrders();
    } catch (actionError) {
      setError(actionError.message);
    } finally {
      setBusyId('');
    }
  };

  if (!session) {
    return <AdminLogin onSignedIn={setSession} />;
  }

  return (
    <main className='admin-page'>
      <header className='admin-hero'>
        <img src='/images/mukwei-cinematic-hero-1600.webp' alt='' />
        <div className='admin-hero-shade' />
        <div className='admin-hero-content'>
          <p className='section-kicker'>Private operations</p>
          <h1>The first pour,<br /><em>managed beautifully.</em></h1>
          <p>Live sales and fulfilment intelligence · Signed in as {session.email}</p>
        </div>
        <div className='admin-header-actions'>
          <button type='button' className='admin-icon-button' onClick={loadOrders} aria-label='Refresh orders'>↻</button>
          <button type='button' className='text-link' onClick={() => { signOutAdmin(); setSession(null); }}>Sign out</button>
        </div>
      </header>

      <section className='sales-summary'>
        <article className='revenue'><i aria-hidden='true'>R</i><span>Gross paid sales</span><strong>{formatMoney(summary.revenue)}</strong><small>Confirmed Yoco payments</small></article>
        <article className='paid'><i aria-hidden='true'>✓</i><span>Paid orders</span><strong>{summary.paid}</strong><small>{summary.bottles} bottles reserved</small></article>
        <article className='abandoned'><i aria-hidden='true'>–</i><span>Abandoned</span><strong>{summary.abandoned}</strong><small>Unpaid for 30+ minutes</small></article>
        <article className='bottles'><i aria-hidden='true'>◆</i><span>Bottles secured</span><strong>{summary.bottles}</strong><small>Across confirmed orders</small></article>
      </section>

      <section className='admin-insights'>
        <article className='admin-insight-card'>
          <div className='insight-heading'><p className='section-kicker'>Conversion</p><span>↗</span></div>
          <strong>{conversion}%</strong>
          <div className='conversion-track'><span style={{ width: `${Math.max(2, conversion)}%` }} /></div>
          <small>Paid orders compared with completed and abandoned checkout attempts.</small>
        </article>
        <article className='admin-insight-card'>
          <div className='insight-heading'><p className='section-kicker'>Fulfilment</p><span>{summary.paid} paid</span></div>
          <div className='fulfilment-lines'>
            <p><i className='gold' />Preparing <strong>{preparing}</strong></p>
            <p><i className='green' />On the way / delivered <strong>{dispatched}</strong></p>
            <p><i className='red' />Needs follow-up <strong>{summary.abandoned}</strong></p>
          </div>
        </article>
      </section>

      <section className='feedback-insights'>
        <div className='feedback-insights-heading'>
          <div>
            <p className='section-kicker'>Checkout intelligence</p>
            <h2>Why customers step away.</h2>
          </div>
          <div className='feedback-response-rate'>
            <strong>{feedbackTotal}</strong>
            <span>responses · {summary.abandoned ? Math.round((feedbackTotal / summary.abandoned) * 100) : 0}% of abandoned</span>
          </div>
        </div>
        <div className='feedback-reason-grid'>
          {feedbackStats.map((item) => (
            <article key={item.key}>
              <div><span>{item.label}</span><strong>{item.count}</strong></div>
              <div className='feedback-track'><i style={{ width: `${item.percent}%` }} /></div>
              <small>{item.percent}% of responses</small>
            </article>
          ))}
        </div>
        {!feedbackTotal && <p className='feedback-empty'>Responses will appear here after customers answer from a follow-up email.</p>}
      </section>

      <section className='admin-command-bar'>
        <div className='admin-tabs'>
          {['all', 'paid', 'abandoned', 'pending'].map((value) => (
            <button key={value} type='button' className={filter === value ? 'active' : ''} onClick={() => setFilter(value)}>
              {value}
              <span>{value === 'all' ? orders.length : orders.filter((order) => order.salesCategory === value).length}</span>
            </button>
          ))}
        </div>
        <button
          type='button'
          className='cta-button primary shipping-broadcast'
          disabled={!summary.paid || busyId === 'broadcast'}
          onClick={() => {
            if (!window.confirm(`Send a shipping update to all ${summary.paid} eligible paid-order customers?`)) return;
            perform(
              'broadcast',
              () => adminRequest('broadcast/shipping', { method: 'POST', body: '{}' }),
              'Shipping update queued for all eligible paid customers.'
            );
          }}
        >
          {busyId === 'broadcast' ? 'Sending update…' : 'Announce shipping start'}
        </button>
      </section>

      {error && <div className='payment-message error' role='alert'>{error}</div>}
      {notice && <div className='payment-message' role='status'>{notice}</div>}

      <section className='orders-panel'>
        <div className='orders-panel-heading'>
          <h2>{filter === 'all' ? 'All sales activity' : `${filter} orders`}</h2>
          <span>{visibleOrders.length} records</span>
        </div>
        {loading ? (
          <div className='admin-loading'><span className='payment-spinner' /> Loading live sales…</div>
        ) : visibleOrders.length === 0 ? (
          <div className='admin-empty'>No {filter} orders yet.</div>
        ) : (
          <div className='admin-orders'>
            {visibleOrders.map((order) => (
              <article className='admin-order-card' key={order.id}>
                <div className='admin-order-main'>
                  <span className={`order-state ${order.salesCategory}`}>{order.salesCategory}</span>
                  <div><h3>{order.fullName || 'Checkout visitor'}</h3><p>{order.id} · {order.email || 'No email captured'}</p></div>
                  <div className='order-total'><strong>{formatMoney(order.amount)}</strong><small>{order.quantity || 0} bottle(s)</small></div>
                </div>
                <div className='admin-order-details'>
                  <p><span>Phone</span>{order.phone || '—'}</p>
                  <p><span>Delivery</span>{order.address || '—'}</p>
                  <p>
                    <span>Email delivery</span>
                    {(order.salesCategory === 'abandoned'
                      ? order.followUpDeliveryStatus
                      : order.emailDeliveryStatus
                    ).replace(/_/g, ' ')}
                  </p>
                  <p><span>Created</span>{order.createdAt ? new Date(order.createdAt).toLocaleString('en-ZA') : '—'}</p>
                </div>
                {order.feedbackReason && (
                  <div className='order-feedback'>
                    <span>Checkout feedback</span>
                    <strong>{order.feedbackLabel}</strong>
                    {order.feedbackDetail && <p>“{order.feedbackDetail}”</p>}
                    <small>{order.feedbackSubmittedAt ? new Date(order.feedbackSubmittedAt).toLocaleString('en-ZA') : ''}</small>
                  </div>
                )}
                <div className='admin-order-actions'>
                  {order.salesCategory === 'paid' && (
                    <>
                      <label>
                        Fulfilment
                        <select
                          value={order.fulfillmentStatus}
                          disabled={busyId === order.id}
                          onChange={(event) => perform(
                            order.id,
                            () => adminRequest(`orders/${order.id}/fulfillment`, {
                              method: 'PATCH',
                              body: JSON.stringify({ status: event.target.value }),
                            }),
                            `${order.id} moved to ${event.target.value}.`
                          )}
                        >
                          {fulfilmentOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                        </select>
                      </label>
                      <button
                        type='button'
                        className='text-link gold'
                        disabled={busyId === order.id}
                        onClick={() => perform(
                          order.id,
                          () => adminRequest(`orders/${order.id}/retry-email`, { method: 'POST', body: '{}' }),
                          `Confirmation resent to ${order.email}.`
                        )}
                      >
                        Resend receipt
                      </button>
                    </>
                  )}
                  {order.salesCategory === 'abandoned' && (
                    <button
                      type='button'
                      className='cta-button secondary'
                      disabled={!order.email || busyId === order.id}
                      onClick={() => perform(
                        order.id,
                        () => adminRequest(`orders/${order.id}/follow-up`, { method: 'POST', body: '{}' }),
                        `Follow-up sent to ${order.email}.`
                      )}
                    >
                      {busyId === order.id ? 'Sending…' : 'Send follow-up'}
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

function AdminLogin({ onSignedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setWorking(true);
    setError('');
    try {
      onSignedIn(await signInAdmin(email, password));
    } catch (signInError) {
      setError(signInError.message);
    } finally {
      setWorking(false);
    }
  };

  return (
    <main className='content-page admin-login-page'>
      <img className='admin-login-image' src='/images/mukwei-cinematic-hero-1600.webp' alt='' />
      <div className='admin-login-shade' />
      <section className='password-card admin-login-card'>
        <span className='brand-mark' aria-hidden='true'>M</span>
        <span className='admin-private-pill'>Private access</span>
        <p className='section-kicker'>Mukwei operations</p>
        <h1>Command the<br />first pour.</h1>
        <p className='section-copy'>Sales, customers and fulfilment—beautifully managed in one secure place.</p>
        <form className='password-form' onSubmit={handleSubmit}>
          <label htmlFor='adminEmail'>Email</label>
          <input id='adminEmail' type='email' autoComplete='username' value={email} onChange={(event) => setEmail(event.target.value)} required />
          <label htmlFor='adminPassword'>Password</label>
          <input id='adminPassword' type='password' autoComplete='current-password' value={password} onChange={(event) => setPassword(event.target.value)} required />
          {error && <p className='receipt-error' role='alert'>{error}</p>}
          <button className='cta-button primary full-width' type='submit' disabled={working}>
            {working ? 'Signing in…' : 'Open dashboard'}
          </button>
        </form>
      </section>
    </main>
  );
}

export default AdminDashboard;
